'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { useCheckAuth } from '@/features/auth/check'

import { useAssent } from '@/entities/assent'
import { useRegion } from '@/entities/regions'
import { useShopsQuery } from '@/entities/shops'
import { useUser } from '@/entities/user'

import { normalizeEmail } from '@/shared/lib/validation/email'
import { formatPhoneMasked } from '@/shared/lib/validation/phone'

import { formRequest, submitServiceMindboxRegister } from '../api/form-request'
import { formSchema, FormSchema } from '../types/types'

export const useServiceForm = (captcha: string) => {
	const { currentRegion } = useRegion()
	const pathname = usePathname()

	const { isAuthorized } = useCheckAuth()
	const { user, isLoading: userInfoIsLoading } = useUser()
	const { data, isLoading: shopsInfoIsLoading } = useShopsQuery()
	const {
		showNewsletterCheckbox,
		isAccepted: isNewsletterAccepted,
		setIsAccepted: setIsNewsletterAccepted,
		checkConsent,
		saveConsent,
		hasConsent
	} = useAssent()

	const [formState, setFormState] = useState({
		isLoading: false,
		isError: false,
		isSuccess: false
	})

	const { control, setValue, handleSubmit, watch } = useForm<FormSchema>({
		mode: 'onBlur',
		resolver: zodResolver(formSchema)
	})

	const userType = watch('USER_TYPE') ?? 'individual'
	const phone = watch('PHONE')

	useEffect(() => {
		checkConsent(phone)
	}, [phone])

	const onSubmit: SubmitHandler<Required<FormSchema>> = async data => {
		if (!captcha) return
		const pageUrl = `https://www.iport.ru${pathname}`
		const formData = {
			...data,
			TYPE: 'ServiceCenter',
			SCRIPT_ID: userType === `legal` ? `665327` : `507197`,
			SEND_URL: pathname
		}

		try {
			setFormState(prev => ({ ...prev, isLoading: true }))
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			await formRequest(formData)
			if (!hasConsent && isNewsletterAccepted) {
				await saveConsent(data.PHONE)
			}

			const selectedServiceCenter = options?.find(option => option.value === data.STORE_ID)?.label || 'Не выбран'
			const fullName = `${data.NAME || ''} ${data.LAST_NAME || ''}`.trim()

			void submitServiceMindboxRegister({
				NAME: fullName,
				PHONE: data.PHONE,
				EMAIL: data.EMAIL || '',
				TITLE: 'Сервисные центры',
				URL: pageUrl,
				SEND_TEXT: selectedServiceCenter,
				IS_INDIVIDUAL: userType === 'individual' ? 'true' : 'false'
			}).catch(error => {
				console.error('Ошибка отправки service mindbox register:', error)
			})

			setFormState(prev => ({ ...prev, isLoading: false, isSuccess: true }))
		} catch (error) {
			setFormState({
				isLoading: false,
				isError: true,
				isSuccess: false
			})
		}
	}

	const onRetry = () => {
		setFormState({
			isLoading: false,
			isError: false,
			isSuccess: false
		})
	}

	const options = data?.data
		?.filter(shop => shop.REGION === currentRegion?.REGION_NAME && shop.UF_IS_SERVICE === '1')
		?.map(shop => ({
			label: shop.NAME.replace(/<[^>]*>/g, ' '), // удаляем все теги
			value: shop.XML_ID
		}))

	useEffect(() => {
		if (user) {
			setValue('NAME', user?.first_name)
			setValue('LAST_NAME', user?.last_name)
			setValue('EMAIL', user?.user_email ? normalizeEmail(user.user_email) : '')
			setValue('PHONE', formatPhoneMasked(user?.user_phone))
		}
	}, [setValue, user])

	return {
		control,
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		onSubmit: handleSubmit(onSubmit),
		availableServiceCenters: options,
		userInfoIsLoading: isAuthorized ? userInfoIsLoading : false,
		shopsInfoIsLoading,
		onRetry,
		formState,
		userType,
		setValue,
		showNewsletterCheckbox,
		isNewsletterAccepted,
		setIsNewsletterAccepted
	}
}
